import type { GeoPoint } from 'screens/TripDetails/TripDetails.types'

export interface CarrierProfile {
  name: string
  address: string
  city: string
  country: string
  registrationTaxNumber?: string
  website?: string
  types: ProfileType[]
  transportModes: TransportMode[]
  qualifications: Qualification[]
  additionalNotes?: string
}

export interface CarrierLogo {
  fileData: string
  fileSize: number
  sha256: string
  contentType: string
}

export enum TransportMode {
  FULL_TRUCKLOAD = 'FULL_TRUCKLOAD',
  LESS_THAN_TRUCKLOAD = 'LESS_THAN_TRUCKLOAD',
  VANS = 'VANS',
  GROUPAGE_LINEHAUL = 'GROUPAGE_LINEHAUL',
}

export enum Qualification {
  GENERAL_CARGO = 'GENERAL_CARGO',
  THERMO_CONTROLLED = 'THERMO_CONTROLLED',
  HEALTHCARE = 'HEALTHCARE',
  DANGEROUS_GOODS = 'DANGEROUS_GOODS',
  SECURITY = 'SECURITY',
  SPECIAL_TRANSPORT = 'SPECIAL_TRANSPORT',
  WASTE_TRANSPORT = 'WASTE_TRANSPORT',
}

export enum ProfileType {
  BRANCH = 'BRANCH',
  FLEET = 'FLEET',
  WAREHOUSE = 'WAREHOUSE',
  CARRIER = 'CARRIER',
  OTHER = 'OTHER',
}

export interface BusinessEntity {
  id: string
  type: ProfileType
  location: string
  registrationTaxNumber: string
}

export interface Contact {
  id: string
  name: string
  roles: ContactRole[]
  location: string
  email: string
  phone: string
}

export enum ContactRole {
  CARRIER_OWNER = 'CARRIER_OWNER',
  OPERATIONS_MANAGER = 'OPERATIONS_MANAGER',
  DISPATCH_COORDINATOR = 'DISPATCH_COORDINATOR',
  FLEET_MANAGER = 'FLEET_MANAGER',
  CUSTOMER_SERVICE_REPRESENTATIVE = 'CUSTOMER_SERVICE_REPRESENTATIVE',
  BILLING_AND_INVOICING_SPECIALIST = 'BILLING_AND_INVOICING_SPECIALIST',
  OTHER = 'OTHER',
}

export interface Equipment {
  id: string
  type: EquipmentType
  weightCapacity?: number
  weightUnit: WeightUnit
  fuelType: FuelType
  quantity?: number
}

export enum WeightUnit {
  METRIC_TONNES = 'METRIC_TONNES',
  TONS = 'TONS',
  KILOGRAMS = 'KILOGRAMS',
  POUNDS = 'POUNDS',
  OTHER = 'OTHER',
}

export enum EquipmentType {
  BOX_TRAILER = 'BOX_TRAILER',
  CURTAIN_TRAILER = 'CURTAIN_TRAILER',
  FLATBED_TRAILER = 'FLATBED_TRAILER',
  MEGA_TRAILER = 'MEGA_TRAILER',
  REEFER_TRAILER = 'REEFER_TRAILER',
  REFRIGERATED_TRAILER = 'REFRIGERATED_TRAILER',
  REFRIGERATED_VAN = 'REFRIGERATED_VAN',
  TRAILER = 'TRAILER',
  TRAILER_TAILLIFT = 'TRAILER_TAILLIFT',
  TRACTOR = 'TRACTOR',
  VAN = 'VAN',
  VAN_TAILLIFT = 'VAN_TAILLIFT',
  OTHER = 'OTHER',
}

export enum FuelType {
  DIESEL = 'DIESEL',
  CNG = 'CNG',
  ELECTRIC = 'ELECTRIC',
  PETROL = 'PETROL',
  LNG = 'LNG',
  NONE = 'NONE',
  OTHER = 'OTHER',
}

export interface Lane {
  id: string
  startLocation: Location
  endLocation: Location
  direction: LaneDirection
  vehiclesAmount?: number
  contacts: Contact[]
}

export const isLane = (value: Lane | Region): value is Lane =>
  'startLocation' in value

export interface Region {
  id: string
  location: Location
  vehiclesAmount?: number
  contacts: Contact[]
}

export const isRegion = (value: Lane | Region): value is Region =>
  'location' in value

export interface Location {
  name: string
  areas: Area[]
}

export interface Area {
  id: string
  name: string
  type: AreaType
}

export interface SourceArea extends Area {
  source: string
  code: string
}

export interface CircleArea extends Area {
  center: GeoPoint
  radius: number
}

export enum AreaType {
  SOURCE = 'SOURCE',
  CIRCLE = 'CIRCLE',
}

export enum LaneDirection {
  ONE_WAY = 'ONE_WAY',
  ROUND_TRIP = 'ROUND_TRIP',
}

export interface CarrierProfileFormValues {
  name: string
  address: string
  city: string
  country: string
  registrationTaxNumber?: string
  website?: string
  types: ProfileType[]
  transportModes: TransportMode[]
  qualifications: Qualification[]
  additionalNotes?: string
  companyLogo?: string[]
}

export interface BusinessEntityFormValues {
  type: ProfileType
  location: string
  registrationTaxNumber: string
}

export interface ContactFormValues {
  name?: string
  roles?: ContactRole[]
  location?: string
  email?: string
  phone?: string
}

export interface EquipmentFormValues {
  weightUnit: WeightUnit
  type: EquipmentType
  weightCapacity: number
  fuelType: FuelType
  quantity: number
}

export interface LaneFormValues {
  startLocation: Location
  endLocation: Location
  direction: LaneDirection
  vehiclesAmount?: number
  contactIds: string[]
}

export interface RegionFormValues {
  location: Location
  vehiclesAmount?: number
  contactIds: string[]
}

export enum CarrierProfileTab {
  LANES_REGIONS = 'LANES_REGIONS',
  COMPANY = 'COMPANY',
  CONTACTS = 'CONTACTS',
  EQUIPMENT = 'EQUIPMENT',
}

export enum LaneOrRegionPanel {
  START = 'START',
  END = 'END',
  ADDITIONAL_INFO = 'ADDITIONAL_INFO',
}
